import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
    Calculator,
    Sparkles,
    ArrowRight,
    CheckCircle2,
    Mail,
    Loader2,
    Info,
} from "lucide-react";
import CTAButtons from "@/components/CTAButtons";
import { submitInquiry } from "@/lib/api";
import { TAX_YEARS, LATEST_YEAR, PERIODS, periodToAnnual, computeTaxYear } from "@/data/taxTables";

const EMAIL_RE = /^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}$/;

const fmtR = (n) =>
    new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR", maximumFractionDigits: 0 }).format(
        Math.round(Number(n) || 0)
    );

/* ---------------------------------------------------------------------- */
/*  MODE + PERIOD RADIO GROUPS                                             */
/* ---------------------------------------------------------------------- */

function ModeSelector({ mode, setMode }) {
    const options = [
        {
            id: "simple",
            title: "Quick and Simple",
            desc: "Just your salary and age. Done in 30 seconds.",
        },
        {
            id: "detailed",
            title: "Complex and Detailed",
            desc: "Add retirement, medical aid, travel allowance & more for a precise picture.",
        },
    ];
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="tax-calc-mode-selector">
            {options.map((o) => {
                const active = mode === o.id;
                return (
                    <label
                        key={o.id}
                        data-testid={`tax-calc-mode-${o.id}`}
                        className={`relative flex flex-col gap-1.5 p-5 border cursor-pointer transition-colors ${
                            active ? "border-tca-orange bg-tca-orangeSoft" : "border-tca-line bg-white hover:border-tca-ink"
                        }`}
                    >
                        <input
                            type="radio"
                            name="tax-calc-mode"
                            value={o.id}
                            checked={active}
                            onChange={() => setMode(o.id)}
                            className="absolute top-4 right-4 w-4 h-4 accent-tca-orange"
                        />
                        <span className={`font-display font-extrabold text-lg ${active ? "text-tca-orange" : "text-tca-ink"}`}>
                            {o.title}
                        </span>
                        <span className="text-sm text-tca-mute leading-snug pr-6">{o.desc}</span>
                    </label>
                );
            })}
        </div>
    );
}

function PeriodSelector({ period, setPeriod }) {
    return (
        <div className="flex flex-wrap gap-2" data-testid="tax-calc-period-selector">
            {PERIODS.map((p) => {
                const active = period === p.id;
                return (
                    <button
                        key={p.id}
                        type="button"
                        onClick={() => setPeriod(p.id)}
                        data-testid={`tax-calc-period-${p.id}`}
                        className={`px-4 py-2.5 text-sm font-semibold border transition-colors ${
                            active
                                ? "bg-tca-ink border-tca-ink text-white"
                                : "bg-white border-tca-line text-tca-ink hover:border-tca-ink"
                        }`}
                    >
                        {p.label}
                    </button>
                );
            })}
        </div>
    );
}

/* ---------------------------------------------------------------------- */
/*  RESULTS                                                                 */
/* ---------------------------------------------------------------------- */

function PeriodBreakdownTable({ periods }) {
    return (
        <div className="overflow-x-auto border border-tca-line" data-testid="tax-calc-period-table">
            <table className="w-full text-sm">
                <thead>
                    <tr className="bg-tca-ink text-white">
                        <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wide">Period</th>
                        <th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wide">Tax</th>
                        <th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wide">Net Pay</th>
                    </tr>
                </thead>
                <tbody>
                    {periods.map((p, i) => (
                        <tr key={p.id} data-testid={`tax-calc-period-row-${p.id}`} className={i % 2 === 0 ? "bg-white" : "bg-tca-fog"}>
                            <td className="px-4 py-3 font-bold text-tca-ink border-t border-tca-line">{p.label}</td>
                            <td className="px-4 py-3 text-right text-red-600 border-t border-tca-line">{fmtR(p.tax)}</td>
                            <td className="px-4 py-3 text-right font-bold text-emerald-600 border-t border-tca-line">{fmtR(p.net)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function YearComparisonTable({ years }) {
    return (
        <div className="overflow-x-auto border border-tca-line" data-testid="tax-calc-year-table">
            <table className="w-full text-sm">
                <thead>
                    <tr className="bg-tca-ink text-white">
                        <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wide">Tax Year</th>
                        <th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wide">Gross</th>
                        <th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wide">Tax</th>
                        <th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wide">Net</th>
                        <th className="text-right px-4 py-3 font-semibold text-xs uppercase tracking-wide">Rate</th>
                    </tr>
                </thead>
                <tbody>
                    {years.map((y, i) => (
                        <tr key={y.yearLabel} data-testid={`tax-calc-year-row-${y.yearLabel.replace("/", "-")}`} className={i % 2 === 0 ? "bg-white" : "bg-tca-fog"}>
                            <td className="px-4 py-3 font-bold text-tca-ink border-t border-tca-line">{y.yearLabel}</td>
                            <td className="px-4 py-3 text-right text-tca-mute border-t border-tca-line">{fmtR(y.annualGross)}</td>
                            <td className="px-4 py-3 text-right text-red-600 border-t border-tca-line">{fmtR(y.annualTax)}</td>
                            <td className="px-4 py-3 text-right font-bold text-emerald-600 border-t border-tca-line">{fmtR(y.annualNet)}</td>
                            <td className="px-4 py-3 text-right text-tca-mute border-t border-tca-line">{y.effectiveRate.toFixed(1)}%</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/* ---------------------------------------------------------------------- */
/*  LEAD CAPTURE — emails the result + branded PDF, mirrors other tools    */
/* ---------------------------------------------------------------------- */

function TaxCalculatorLeadForm({ mode, period, currentYearResult, yearResults, periodRows, inputAnswers }) {
    const [form, setForm] = useState({ name: "", email: "", phone: "", company: "" });
    const [status, setStatus] = useState("idle"); // idle | loading | success

    const emailTrim = form.email.trim();
    const nameTrim = form.name.trim();
    const emailValid = EMAIL_RE.test(emailTrim);
    const nameValid = nameTrim.length >= 2;
    const showEmailError = emailTrim.length > 0 && !emailValid;
    const canSubmit = nameValid && emailValid && status !== "loading";

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!nameValid || !emailValid) return;
        setStatus("loading");
        try {
            await submitInquiry({
                name: form.name.trim(),
                email: form.email.trim(),
                phone: form.phone.trim() || null,
                company: form.company.trim() || null,
                subject: `Tax Calculator Result — ${LATEST_YEAR.label}`,
                message: `${form.name.trim()} used the Personal Tax Calculator (${mode === "simple" ? "Quick and Simple" : "Complex and Detailed"} mode). Estimated ${LATEST_YEAR.label} annual net pay: R${Math.round(currentYearResult.annualNet)}.`,
                source: "tax-calculator",
                tax_calculator: {
                    mode,
                    period,
                    answers: inputAnswers,
                    years: yearResults.map((y) => ({
                        year_label: y.yearLabel,
                        annual_gross: Math.round(y.annualGross),
                        annual_tax: Math.round(y.annualTax),
                        annual_net: Math.round(y.annualNet),
                        effective_rate: y.effectiveRate,
                    })),
                    periods: periodRows.map((p) => ({
                        period_label: p.label,
                        tax: p.tax,
                        net: p.net,
                    })),
                    latest_year_label: LATEST_YEAR.label,
                },
            });
            setStatus("success");
            toast.success("Your full tax report is on its way — check your inbox.");
        } catch (err) {
            console.error(err);
            setStatus("idle");
            toast.error("Something went wrong. Please try again or contact us directly.");
        }
    };

    if (status === "success") {
        return (
            <div data-testid="tax-calc-lead-success" className="bg-white border border-tca-orange p-7 md:p-9 text-center">
                <div className="w-12 h-12 mx-auto bg-tca-orange/10 flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-6 h-6 text-tca-orange" />
                </div>
                <h3 className="font-display font-extrabold text-tca-ink text-2xl mb-2">
                    Thanks {form.name.split(" ")[0]} — your report is on its way.
                </h3>
                <p className="text-tca-mute leading-relaxed max-w-lg mx-auto">
                    We've emailed your full tax calculator report (with a branded PDF) to {form.email}. A TC Auditors
                    advisor will follow up within one business day if you'd like help fine-tuning it.
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={onSubmit} data-testid="tax-calc-lead-form" className="bg-white border border-tca-line p-7 md:p-9" noValidate>
            <div className="flex items-start gap-4 mb-6">
                <div className="w-10 h-10 bg-tca-orange/10 flex items-center justify-center shrink-0">
                    <Mail className="w-5 h-5 text-tca-orange" />
                </div>
                <div>
                    <h3 className="font-display font-extrabold text-tca-ink text-2xl leading-tight">
                        Email me my full report
                    </h3>
                    <p className="text-sm text-tca-mute mt-1">
                        We'll send you a branded PDF with your period breakdown and three-year comparison, and a TC
                        Auditors advisor will follow up. No spam, no commitment.
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs uppercase tracking-[0.16em] font-bold text-tca-mute mb-1.5">
                        Full name <span className="text-tca-orange">*</span>
                    </label>
                    <input
                        type="text"
                        value={form.name}
                        onChange={update("name")}
                        data-testid="tax-calc-lead-name"
                        className="w-full px-4 py-3 border border-tca-line bg-white text-tca-ink focus:outline-none focus:border-tca-orange transition-colors"
                        placeholder="Jane Smith"
                    />
                </div>
                <div>
                    <label className="block text-xs uppercase tracking-[0.16em] font-bold text-tca-mute mb-1.5">
                        Email <span className="text-tca-orange">*</span>
                    </label>
                    <input
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        data-testid="tax-calc-lead-email"
                        aria-invalid={showEmailError}
                        className={`w-full px-4 py-3 border bg-white text-tca-ink focus:outline-none transition-colors ${
                            showEmailError ? "border-red-500 bg-red-50 focus:border-red-500" : "border-tca-line focus:border-tca-orange"
                        }`}
                        placeholder="jane@company.co.za"
                    />
                    {showEmailError && (
                        <p className="text-xs text-red-600 mt-1" data-testid="tax-calc-lead-email-error">
                            Please enter a valid email address.
                        </p>
                    )}
                </div>
                <div>
                    <label className="block text-xs uppercase tracking-[0.16em] font-bold text-tca-mute mb-1.5">
                        Phone <span className="text-tca-mute/60 normal-case tracking-normal font-normal">(optional)</span>
                    </label>
                    <input
                        type="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        data-testid="tax-calc-lead-phone"
                        className="w-full px-4 py-3 border border-tca-line bg-white text-tca-ink focus:outline-none focus:border-tca-orange transition-colors"
                        placeholder="079 461 7545"
                    />
                </div>
                <div>
                    <label className="block text-xs uppercase tracking-[0.16em] font-bold text-tca-mute mb-1.5">
                        Company <span className="text-tca-mute/60 normal-case tracking-normal font-normal">(optional)</span>
                    </label>
                    <input
                        type="text"
                        value={form.company}
                        onChange={update("company")}
                        data-testid="tax-calc-lead-company"
                        className="w-full px-4 py-3 border border-tca-line bg-white text-tca-ink focus:outline-none focus:border-tca-orange transition-colors"
                        placeholder="Acme (Pty) Ltd"
                    />
                </div>
            </div>

            <button
                type="submit"
                disabled={!canSubmit}
                aria-disabled={!canSubmit}
                data-testid="tax-calc-lead-submit"
                className="btn-primary group mt-6 w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
            >
                {status === "loading" ? (
                    <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Sending…
                    </>
                ) : (
                    <>
                        Email Me My Full Tax Report
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                    </>
                )}
            </button>

            <p className="text-[11px] text-tca-mute mt-4">
                By submitting, you consent to TC Auditors contacting you about your inquiry. We will never share your
                details.
            </p>
        </form>
    );
}

/* ---------------------------------------------------------------------- */
/*  MAIN PAGE                                                               */
/* ---------------------------------------------------------------------- */

export default function TaxCalculator() {
    const [mode, setMode] = useState("simple");
    const [period, setPeriod] = useState("monthly");
    const [age, setAge] = useState("");
    const [salaryInput, setSalaryInput] = useState("");

    const [retirementInputMode, setRetirementInputMode] = useState("pct"); // pct | rand
    const [retirementPct, setRetirementPct] = useState("");
    const [retirementRandMonthly, setRetirementRandMonthly] = useState("");
    const [medicalMembers, setMedicalMembers] = useState("");
    const [travelAllowanceMonthly, setTravelAllowanceMonthly] = useState("");
    const [otherIncomeAnnual, setOtherIncomeAnnual] = useState("");
    const [uifOptOut, setUifOptOut] = useState(false);

    const ageNum = Math.max(0, Number(age) || 0);
    const baseAnnualSalary = periodToAnnual(salaryInput, period);
    const isDetailed = mode === "detailed";

    const retirementContributionAnnual = isDetailed
        ? retirementInputMode === "pct"
            ? baseAnnualSalary * (Math.max(0, Number(retirementPct) || 0) / 100)
            : Math.max(0, Number(retirementRandMonthly) || 0) * 12
        : 0;
    const medicalMembersNum = isDetailed ? Math.max(0, Number(medicalMembers) || 0) : 0;
    const travelAllowanceAnnual = isDetailed ? Math.max(0, Number(travelAllowanceMonthly) || 0) * 12 : 0;
    const otherIncomeAnnualNum = isDetailed ? Math.max(0, Number(otherIncomeAnnual) || 0) : 0;
    const uifOptOutActive = isDetailed && uifOptOut;

    const showResults = ageNum > 0 && baseAnnualSalary > 0;

    const yearResults = useMemo(() => {
        if (!showResults) return [];
        return TAX_YEARS.map((year) =>
            computeTaxYear(year, {
                baseAnnualSalary,
                otherIncomeAnnual: otherIncomeAnnualNum,
                travelAllowanceAnnual,
                retirementContributionAnnual,
                medicalMembers: medicalMembersNum,
                age: ageNum,
                uifOptOut: uifOptOutActive,
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showResults, baseAnnualSalary, otherIncomeAnnualNum, travelAllowanceAnnual, retirementContributionAnnual, medicalMembersNum, ageNum, uifOptOutActive]);

    const currentYearResult = yearResults[yearResults.length - 1];

    const periodRows = useMemo(() => {
        if (!currentYearResult) return [];
        return PERIODS.map((p) => ({
            id: p.id,
            label: p.label,
            tax: currentYearResult.annualTax / p.perYear,
            net: currentYearResult.annualNet / p.perYear,
        }));
    }, [currentYearResult]);

    const selectedPeriodLabel = PERIODS.find((p) => p.id === period)?.label || "Monthly";
    const selectedPeriodRow = periodRows.find((p) => p.id === period);

    const inputAnswers = useMemo(() => {
        const answers = [
            { question: "Mode", answer: mode === "simple" ? "Quick and Simple" : "Complex and Detailed" },
            { question: "Salary period", answer: selectedPeriodLabel },
            { question: `Gross ${selectedPeriodLabel} salary`, answer: fmtR(salaryInput) },
            { question: "Age", answer: String(ageNum) },
        ];
        if (isDetailed) {
            answers.push({
                question: "Retirement contribution",
                answer:
                    retirementInputMode === "pct"
                        ? `${Number(retirementPct) || 0}% of salary`
                        : `${fmtR(retirementRandMonthly)} per month`,
            });
            answers.push({ question: "Medical aid members (incl. self)", answer: String(medicalMembersNum) });
            answers.push({ question: "Travel allowance (monthly)", answer: fmtR(travelAllowanceMonthly) });
            answers.push({ question: "Other income / bonus (annual)", answer: fmtR(otherIncomeAnnual) });
            answers.push({ question: "UIF", answer: uifOptOut ? "Opted out" : "Contributing" });
        }
        return answers;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, selectedPeriodLabel, salaryInput, ageNum, isDetailed, retirementInputMode, retirementPct, retirementRandMonthly, medicalMembersNum, travelAllowanceMonthly, otherIncomeAnnual, uifOptOut]);

    return (
        <div data-testid="page-tax-calculator">
            {/* Hero */}
            <section className="relative overflow-hidden bg-white border-b border-tca-line">
                <div className="absolute inset-0 grid-bg pointer-events-none opacity-70" />
                <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-tca-orangeSoft blur-3xl opacity-70 pointer-events-none" />
                <div className="container-tca relative pt-16 pb-20 md:pt-24 md:pb-28 max-w-4xl">
                    <div className="flex items-center gap-3 mb-7" data-testid="tax-calculator-badge">
                        <span className="inline-block w-8 h-px bg-tca-orange" />
                        <p className="overline text-tca-orange">Free Tool &middot; Updated for the 2026/2027 Tax Year</p>
                    </div>
                    <h1
                        data-testid="tax-calculator-title"
                        className="font-display font-extrabold text-tca-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.02em]"
                    >
                        Your Personal Tax Calculator
                    </h1>
                    <p
                        data-testid="tax-calculator-slogan"
                        className="mt-6 text-xl sm:text-2xl font-display font-bold text-tca-ink leading-snug"
                    >
                        Know exactly what SARS expects — before they tell you.
                    </p>
                    <p
                        data-testid="tax-calculator-description"
                        className="mt-4 text-base sm:text-lg text-tca-mute max-w-2xl leading-relaxed"
                    >
                        Work out your estimated personal income tax liability in under two minutes. No jargon, no
                        guesswork, no sign-up walls — just clear numbers you can actually plan around, built and
                        checked by real Chartered Accountants.
                    </p>
                    <CTAButtons className="mt-9" testIdPrefix="tax-calculator-hero-cta" />
                </div>
            </section>

            {/* Calculator body */}
            <section className="bg-tca-fog border-t border-tca-line">
                <div className="container-tca py-16 md:py-20 max-w-4xl space-y-10">
                    {/* Step 1 — Mode */}
                    <div data-testid="tax-calc-step-mode">
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-tca-orange mb-3">Step 1 &middot; Choose your level of detail</p>
                        <ModeSelector mode={mode} setMode={setMode} />
                    </div>

                    {/* Step 2 — Period */}
                    <div data-testid="tax-calc-step-period">
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-tca-orange mb-3">
                            Step 2 &middot; How often do you get paid?
                        </p>
                        <PeriodSelector period={period} setPeriod={setPeriod} />
                    </div>

                    {/* Step 3 — Core inputs */}
                    <div className="bg-white border border-tca-line p-6 sm:p-8" data-testid="tax-calc-step-inputs">
                        <p className="text-xs font-bold uppercase tracking-[0.16em] text-tca-orange mb-5">Step 3 &middot; Your details</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-xs uppercase tracking-[0.14em] font-bold text-tca-mute mb-1.5">Your age</label>
                                <input
                                    type="number"
                                    min="0"
                                    inputMode="numeric"
                                    value={age}
                                    onChange={(e) => setAge(e.target.value)}
                                    data-testid="tax-calc-age-input"
                                    className="w-full px-4 py-3 border border-tca-line bg-white text-tca-ink focus:outline-none focus:border-tca-orange transition-colors"
                                    placeholder="e.g. 34"
                                />
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-[0.14em] font-bold text-tca-mute mb-1.5">
                                    Your gross {selectedPeriodLabel.toLowerCase()} salary (R)
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    inputMode="decimal"
                                    value={salaryInput}
                                    onChange={(e) => setSalaryInput(e.target.value)}
                                    data-testid="tax-calc-salary-input"
                                    className="w-full px-4 py-3 border border-tca-line bg-white text-tca-ink focus:outline-none focus:border-tca-orange transition-colors"
                                    placeholder="e.g. 45000"
                                />
                            </div>
                        </div>

                        {isDetailed && (
                            <div className="mt-7 pt-7 border-t border-tca-line grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="tax-calc-detailed-fields">
                                <div className="sm:col-span-2">
                                    <label className="block text-xs uppercase tracking-[0.14em] font-bold text-tca-mute mb-1.5">
                                        Retirement fund contribution
                                    </label>
                                    <div className="flex flex-wrap items-center gap-2 mb-2">
                                        <button
                                            type="button"
                                            onClick={() => setRetirementInputMode("pct")}
                                            data-testid="tax-calc-retirement-mode-pct"
                                            className={`px-3 py-1.5 text-xs font-semibold border ${
                                                retirementInputMode === "pct" ? "bg-tca-ink text-white border-tca-ink" : "bg-white border-tca-line text-tca-ink"
                                            }`}
                                        >
                                            % of salary
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setRetirementInputMode("rand")}
                                            data-testid="tax-calc-retirement-mode-rand"
                                            className={`px-3 py-1.5 text-xs font-semibold border ${
                                                retirementInputMode === "rand" ? "bg-tca-ink text-white border-tca-ink" : "bg-white border-tca-line text-tca-ink"
                                            }`}
                                        >
                                            Fixed Rand amount
                                        </button>
                                    </div>
                                    {retirementInputMode === "pct" ? (
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={retirementPct}
                                            onChange={(e) => setRetirementPct(e.target.value)}
                                            data-testid="tax-calc-retirement-pct-input"
                                            className="w-full px-4 py-3 border border-tca-line bg-white text-tca-ink focus:outline-none focus:border-tca-orange transition-colors"
                                            placeholder="e.g. 7.5 (%)"
                                        />
                                    ) : (
                                        <input
                                            type="number"
                                            min="0"
                                            value={retirementRandMonthly}
                                            onChange={(e) => setRetirementRandMonthly(e.target.value)}
                                            data-testid="tax-calc-retirement-rand-input"
                                            className="w-full px-4 py-3 border border-tca-line bg-white text-tca-ink focus:outline-none focus:border-tca-orange transition-colors"
                                            placeholder="R per month"
                                        />
                                    )}
                                </div>

                                <div>
                                    <label className="block text-xs uppercase tracking-[0.14em] font-bold text-tca-mute mb-1.5">
                                        Medical aid members (incl. yourself)
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={medicalMembers}
                                        onChange={(e) => setMedicalMembers(e.target.value)}
                                        data-testid="tax-calc-medical-members-input"
                                        className="w-full px-4 py-3 border border-tca-line bg-white text-tca-ink focus:outline-none focus:border-tca-orange transition-colors"
                                        placeholder="0 = no medical aid"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs uppercase tracking-[0.14em] font-bold text-tca-mute mb-1.5">
                                        Travel allowance (R per month)
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={travelAllowanceMonthly}
                                        onChange={(e) => setTravelAllowanceMonthly(e.target.value)}
                                        data-testid="tax-calc-travel-input"
                                        className="w-full px-4 py-3 border border-tca-line bg-white text-tca-ink focus:outline-none focus:border-tca-orange transition-colors"
                                        placeholder="e.g. 3000"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs uppercase tracking-[0.14em] font-bold text-tca-mute mb-1.5">
                                        Other income / bonus (R per year)
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={otherIncomeAnnual}
                                        onChange={(e) => setOtherIncomeAnnual(e.target.value)}
                                        data-testid="tax-calc-other-income-input"
                                        className="w-full px-4 py-3 border border-tca-line bg-white text-tca-ink focus:outline-none focus:border-tca-orange transition-colors"
                                        placeholder="e.g. 20000"
                                    />
                                </div>

                                <div className="sm:col-span-2 flex items-center gap-2.5 pt-1">
                                    <input
                                        type="checkbox"
                                        id="tax-calc-uif-optout"
                                        checked={uifOptOut}
                                        onChange={(e) => setUifOptOut(e.target.checked)}
                                        data-testid="tax-calc-uif-optout-checkbox"
                                        className="w-4 h-4 accent-tca-orange"
                                    />
                                    <label htmlFor="tax-calc-uif-optout" className="text-sm text-tca-mute">
                                        I don't pay UIF (e.g. director / exempt)
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Results */}
                    {showResults && currentYearResult && (
                        <div className="space-y-8" data-testid="tax-calc-results">
                            {/* Headline card */}
                            <div className="bg-tca-ink text-white p-7 sm:p-9 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6" data-testid="tax-calc-headline-card">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-tca-orange mb-2">
                                        Your estimated {selectedPeriodLabel.toLowerCase()} take-home pay ({LATEST_YEAR.label})
                                    </p>
                                    <p className="font-display font-extrabold text-4xl sm:text-5xl" data-testid="tax-calc-headline-net">
                                        {fmtR(selectedPeriodRow?.net)}
                                    </p>
                                </div>
                                <div className="sm:text-right">
                                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-white/50 mb-2">
                                        {selectedPeriodLabel} tax
                                    </p>
                                    <p className="font-display font-extrabold text-2xl sm:text-3xl text-red-400" data-testid="tax-calc-headline-tax">
                                        {fmtR(selectedPeriodRow?.tax)}
                                    </p>
                                </div>
                            </div>

                            {/* Period breakdown */}
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.16em] text-tca-orange mb-3">
                                    {LATEST_YEAR.label} &mdash; Tax &amp; Net Pay By Period
                                </p>
                                <PeriodBreakdownTable periods={periodRows} />
                            </div>

                            {/* 3-year comparison */}
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <Sparkles className="w-4 h-4 text-tca-orange" />
                                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-tca-orange">
                                        Three-Year Comparison &mdash; See How The Tax Tables Have Moved
                                    </p>
                                </div>
                                <YearComparisonTable years={yearResults} />
                            </div>

                            {/* Disclaimer */}
                            <div className="flex items-start gap-3 bg-white border border-tca-line p-5" data-testid="tax-calc-disclaimer">
                                <Info className="w-4 h-4 text-tca-mute mt-0.5 shrink-0" />
                                <p className="text-xs text-tca-mute leading-relaxed">
                                    This is an estimate for guidance purposes only, based on the publicly published SARS
                                    tax tables, rebates, medical scheme fees tax credits, UIF ceilings and retirement fund
                                    deduction limits. It does not constitute tax advice and does not account for every
                                    possible deduction or circumstance. Speak to one of our Chartered Accountants for a
                                    tailored assessment.
                                </p>
                            </div>

                            {/* Lead capture */}
                            <TaxCalculatorLeadForm
                                mode={mode}
                                period={period}
                                currentYearResult={currentYearResult}
                                yearResults={yearResults}
                                periodRows={periodRows}
                                inputAnswers={inputAnswers}
                            />
                        </div>
                    )}

                    {!showResults && (
                        <div
                            data-testid="tax-calculator-awaiting-input"
                            className="text-center border border-dashed border-tca-line bg-white p-10 sm:p-14"
                        >
                            <div className="w-14 h-14 mx-auto rounded-full bg-tca-orangeSoft flex items-center justify-center mb-5">
                                <Calculator className="w-6 h-6 text-tca-orange" />
                            </div>
                            <p className="font-display font-bold text-tca-ink text-lg">
                                Enter your age and salary above to see your results.
                            </p>
                        </div>
                    )}
                </div>
            </section>
        </div>
    );
}
